import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useId, useState, type ReactNode } from 'react';

import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { Campo } from '@/componentes/Campo';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { ErroDaLista } from '@/componentes/ErroDaLista';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { RodapeDeContagem, Tabela, type Coluna } from '@/componentes/Tabela';
import { HistoriaNaMesa } from '@/historias/mesa';
import { formatarDataHora, formatarInteiro } from '@/lib/formato';
import { explicarFalha, SemSessao } from '@/lib/pedido';
import { retornoDe, type RetornoDeclarado } from '@/lib/retorno';

import {
  api,
  type ConfiguracaoDaIaDaPlataforma,
  type EtapaDoTeste,
  type ParametrosDaIa,
  type ProtocoloDaIa,
  type ResultadoDoTesteDaIa,
} from './api';
import {
  AJUDA_DO_ENDERECO,
  avisosDoRascunho,
  erroDoEndereco,
  erroDoModelo,
  indiceEmUso,
  leituraEmUso,
  MAIOR_ENDERECO,
  mudou,
  normalizarEndereco,
  OPCAO_DO_PROTOCOLO,
  paraPedido,
  passouNoTeste,
  porQueNaoSalva,
  porQueNaoTesta,
  PROTOCOLOS,
  rascunhoInicial,
  ROTULO_DA_ORIGEM,
  ROTULO_DO_PROTOCOLO,
  soResponde,
  type RascunhoDaIa,
  type TesteFeito,
  type ValorEmUso,
} from './configuracao-da-ia';
import { AcaoComMotivo, Bloco, Recado } from './Pecas';
import { SeletorDeModelo, type EstadoDaLista } from './SeletorDeModelo';

/**
 * Configurações do sistema: o que vale para TODAS as empresas. Por enquanto,
 * o motor de IA (decisão do dono, 27/09/2026: "os modelos de IA eu defino em
 * plataforma"; migração 0042).
 *
 * O que se escolhe aqui: protocolo, endereço e os três modelos. O que NÃO se
 * escolhe aqui: a chave. Ela mora só em IA_CHAVE, no servidor, e a tela diz
 * apenas se ela está lá — nunca a pede, nunca a mostra, nem o começo dela.
 *
 * Salvo aqui, vale no lugar das variáveis do servidor, inteiro (não campo a
 * campo), em até 30 s e sem reiniciar. Sem nada salvo, valem as variáveis,
 * como antes: o QAS não muda até alguém salvar no QAS.
 *
 * O endereço é o destino da chave e dos dados das empresas. Por isso o
 * servidor só aceita https com nome público, o teste e a troca ficam na trilha
 * da plataforma com motivo, e nenhum endereço de provedor vem escrito na tela
 * (H12): quem administra digita o do guia do provedor ou do runbook.
 */

const CHAVE_DA_IA = ['plataforma', 'ia', 'configuracao'] as const;

/**
 * 401 aqui é a sessão de PLATAFORMA vencida: a conferência de `eu` volta a
 * perguntar, e a página cai na entrada da administração — o mesmo caminho de
 * quem abre a plataforma sem sessão.
 */
function useVoltaAEntradaSemSessao() {
  const cliente = useQueryClient();
  return useCallback(
    (erro: unknown) => {
      if (erro instanceof SemSessao) void cliente.invalidateQueries({ queryKey: ['plataforma', 'eu'] });
    },
    [cliente],
  );
}

export function ConfiguracoesDoSistema() {
  const voltarSemSessao = useVoltaAEntradaSemSessao();
  const config = useQuery({
    queryKey: CHAVE_DA_IA,
    queryFn: () => api.configuracaoDaIa(),
    retry: (tentativas, erro) => !(erro instanceof SemSessao) && tentativas < 1,
  });

  useEffect(() => {
    voltarSemSessao(config.error);
  }, [config.error, voltarSemSessao]);

  return (
    <>
      <CabecalhoPagina
        sobretitulo="Administração da plataforma"
        titulo="Configurações do sistema"
        subtitulo="O que vale para todas as empresas: o motor de IA e se este ambiente é de homologação."
      />
      {config.isPending ? <CarregandoAConfiguracao /> : null}
      {config.isError && !config.data ? (
        <ErroDaLista
          oQue="As configurações da IA"
          erro={config.error}
          aoTentarDeNovo={() => {
            void config.refetch();
          }}
        />
      ) : null}
      {config.data ? <MotorDeIa config={config.data} /> : null}
      <AmbienteDeHomologacao />
    </>
  );
}

/**
 * O ambiente de homologação (0080; manual p.44): liga o modo histórias. O QAS
 * liga, a produção fica desligada — e aí o interruptor nem aparece para as
 * empresas. Cada troca pede o motivo, que vai para a trilha da plataforma.
 */
function AmbienteDeHomologacao() {
  const cliente = useQueryClient();
  const ambiente = useQuery({ queryKey: ['plataforma', 'ambiente'], queryFn: () => api.ambiente(), retry: false });
  const definir = useMutation({
    mutationFn: (p: { homologacao: boolean; motivo: string }) => api.definirAmbiente(p.homologacao, p.motivo),
    meta: {
      retorno: retornoDe<{ homologacao: boolean }, { motivo: string }>((r, p) => ({
        titulo: r.homologacao ? 'Homologação ligada' : 'Homologação desligada',
        alvo: { nome: 'Ambiente do sistema' },
        fatos: [`motivo: ${p.motivo}`],
        efeito: r.homologacao ? 'O modo histórias pode ser ligado pelas empresas' : 'O modo histórias some para todas as empresas',
      })),
    },
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['plataforma', 'ambiente'] });
    },
  });
  const ligado = ambiente.data?.homologacao ?? false;
  return (
    <Bloco
      titulo="Ambiente de homologação"
      acoes={
        ambiente.data ? (
          <AcaoComMotivo
            rotulo={ligado ? 'Desligar homologação' : 'Ligar homologação'}
            nivel="secundario"
            pergunta={ligado ? 'Desligar o modo de homologação neste ambiente?' : 'Ligar o modo de homologação neste ambiente?'}
            consequencia={
              ligado
                ? 'O interruptor de histórias some para todas as empresas deste ambiente. As conferências feitas ficam guardadas.'
                : 'Todas as empresas deste ambiente passam a ver o interruptor de histórias de usuário. Faça isso só no ambiente de teste, nunca na produção.'
            }
            rotuloConfirmar={ligado ? 'Desligar homologação' : 'Ligar homologação'}
            pendente={definir.isPending}
            ajudaDoMotivo="O motivo vai para a trilha da plataforma, com pelo menos 10 caracteres."
            aoConfirmar={(motivo) => {
              definir.mutate({ homologacao: !ligado, motivo });
            }}
          />
        ) : null
      }
    >
      {ambiente.isError ? (
        <ErroDaLista
          oQue="Os dados do ambiente"
          erro={ambiente.error}
          aoTentarDeNovo={() => {
            void ambiente.refetch();
          }}
        />
      ) : !ambiente.data ? (
        <Carregando oQue="Carregando o ambiente" className="flex flex-col gap-2">
          <Barra className="h-3 w-2/3" />
        </Carregando>
      ) : (
        <p className="txt-corpo text-tinta-secundaria">
          {ligado
            ? 'Este ambiente é de homologação: o modo histórias (manual, p.44 a p.47) aparece no celular, em Mais › Histórias de usuário.'
            : 'Este ambiente não é de homologação: o modo histórias não aparece para ninguém, como deve ser na produção.'}
        </p>
      )}
    </Bloco>
  );
}

/** Os pares de "IA em uso agora" e os campos do motor: os rótulos já se sabem. */
const PARES_EM_USO = ['Origem', 'Protocolo', 'Endereço', 'Responder', 'Ler arquivos', 'Índice por sentido', 'Chave no servidor'];
const CAMPOS_DO_MOTOR = [
  { rotulo: 'Protocolo', largo: false, opcional: false },
  { rotulo: 'Endereço da API', largo: true, opcional: false },
  { rotulo: 'Modelo para responder', largo: false, opcional: false },
  { rotulo: 'Modelo para ler arquivos', largo: false, opcional: true },
  { rotulo: 'Modelo de vetores', largo: false, opcional: true },
];

/**
 * Carregando (p.32) na forma da tela, e não a frase em cima de três barras: os
 * dois blocos com o título e os rótulos de verdade, e só o que vem do servidor
 * vira barra. Uma região de status só — a do primeiro bloco; o segundo é
 * desenho.
 */
function CarregandoAConfiguracao() {
  return (
    <>
      <Carregando oQue="Carregando a configuração da IA" className="cartao flex flex-col gap-4 p-6">
        <div aria-hidden="true" className="flex flex-col gap-4">
          <h2 className="txt-secao text-tinta">IA em uso agora</h2>
          <dl className="grid grid-cols-[repeat(auto-fill,minmax(var(--campo-min),1fr))] gap-x-6 gap-y-4">
            {PARES_EM_USO.map((rotulo, i) => (
              <div key={rotulo} className="flex min-w-0 flex-col gap-1">
                <dt className="txt-legenda font-normal text-tinta-secundaria">{rotulo}</dt>
                <dd>
                  <Barra className={`my-1 h-3 ${i % 3 === 1 ? 'w-1/2' : 'w-3/4'}`} />
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </Carregando>
      <div aria-hidden="true">
        <Bloco titulo="Motor de IA">
          <div className="formulario-grade">
            {CAMPOS_DO_MOTOR.map((campo) => (
              <div key={campo.rotulo} className={`flex min-w-0 flex-col gap-2 ${campo.largo ? 'campo-largo' : ''}`}>
                <span className="flex flex-wrap items-baseline gap-x-1">
                  <span className="txt-rotulo-campo">{campo.rotulo}</span>
                  {campo.opcional ? <span className="txt-ajuda">· opcional</span> : null}
                </span>
                <Barra className="h-[var(--controle-m)] w-full rounded-controle" />
                <Barra className="my-1 h-2 w-4/5" />
              </div>
            ))}
          </div>
        </Bloco>
      </div>
    </>
  );
}

function MotorDeIa({ config }: { readonly config: ConfiguracaoDaIaDaPlataforma }) {
  const cliente = useQueryClient();
  const voltarSemSessao = useVoltaAEntradaSemSessao();
  const idDoProtocolo = useId();
  const inicial = rascunhoInicial(config);
  const [rascunho, setRascunho] = useState<RascunhoDaIa>(inicial);
  /* A lista é pedida pelo endereço CONFIRMADO — ao sair do campo —, e não a
     cada tecla: a base ainda sem a versão no fim já é endereço válido, e cada
     meio caminho viraria uma consulta ao provedor. */
  const [enderecoDaLista, setEnderecoDaLista] = useState(() => normalizarEndereco(inicial.endereco));
  /* Trocar a versão remonta os campos: o erro de validação de antes some com
     o rascunho descartado ou salvo. */
  const [versao, setVersao] = useState(0);
  const [teste, setTeste] = useState<TesteFeito | null>(null);

  function recomecar(de: RascunhoDaIa) {
    setRascunho(de);
    setEnderecoDaLista(normalizarEndereco(de.endereco));
    setVersao((v) => v + 1);
  }

  function mudar(parcial: Partial<RascunhoDaIa>) {
    setRascunho((r) => ({ ...r, ...parcial }));
  }

  const aoConcluir = (nova: ConfiguracaoDaIaDaPlataforma) => {
    cliente.setQueryData(CHAVE_DA_IA, nova);
    recomecar(rascunhoInicial(nova));
    void cliente.invalidateQueries({ queryKey: ['plataforma'] });
  };
  /* Retorno de ação é toast (R04, p.32), e não uma frase solta no pé da
     página; a recusa sai pelo toast de falha da camada de chamadas. */
  const retornoDaIa = (titulo: string, efeito: string) =>
    retornoDe<ConfiguracaoDaIaDaPlataforma, { motivo: string } | string>((nova, v): RetornoDeclarado => {
      const salva = nova.salva;
      return {
        titulo,
        alvo: { nome: 'Configuração da IA' },
        fatos: [salva ? `${salva.protocolo} · ${salva.modelo}` : null, `motivo: ${typeof v === 'string' ? v : v.motivo}`],
        efeito,
      };
    });

  const salvar = useMutation({
    mutationFn: (p: { readonly parametros: ParametrosDaIa; readonly motivo: string }) =>
      api.salvarConfiguracaoDaIa(p.parametros, p.motivo),
    meta: { retorno: retornoDaIa('Configuração da IA salva', 'Vale para todas as empresas em até 30 segundos') },
    onSuccess: aoConcluir,
    onError: voltarSemSessao,
  });
  const remover = useMutation({
    mutationFn: (motivo: string) => api.removerConfiguracaoDaIa(motivo),
    meta: { retorno: retornoDaIa('Configuração da IA removida', 'A IA volta às variáveis do servidor em até 30 segundos') },
    onSuccess: aoConcluir,
    onError: voltarSemSessao,
  });
  const testar = useMutation({
    mutationFn: (parametros: ParametrosDaIa) => api.testarIa(parametros),
    /* O resultado de cada etapa fica na tabela do teste; o toast só diz que terminou. */
    meta: {
      retorno: retornoDe<ResultadoDoTesteDaIa, ParametrosDaIa>((r, p) => {
        const etapas = [r.resposta, r.leitura, r.vetores].filter((e): e is NonNullable<typeof e> => e !== null);
        const falharam = etapas.filter((e) => !e.ok).length;
        return {
          titulo: 'Teste da configuração concluído',
          tom: falharam > 0 ? 'ressalva' : 'sucesso',
          alvo: { nome: `${p.protocolo} · ${p.modelo}` },
          fatos: [
            `${formatarInteiro(etapas.length - falharam)} de ${formatarInteiro(etapas.length)} ${etapas.length === 1 ? 'etapa respondeu' : 'etapas responderam'}`,
          ],
          efeito: 'O resultado de cada etapa está no bloco do teste',
        };
      }),
    },
    onSuccess: (resultado, parametros) => {
      setTeste({ parametros, resultado });
      void cliente.invalidateQueries({ queryKey: ['plataforma'] });
    },
    onError: voltarSemSessao,
  });

  const protocolo = rascunho.protocolo;
  const naoLista = soResponde(protocolo);
  const enderecoListavel = erroDoEndereco(enderecoDaLista) === null;
  const modelos = useQuery({
    queryKey: ['plataforma', 'ia', 'modelos', protocolo, enderecoDaLista],
    queryFn: () => api.modelosDaIa(protocolo, enderecoDaLista),
    enabled: !naoLista && enderecoListavel,
    staleTime: 10 * 60_000,
    retry: false,
  });
  useEffect(() => {
    voltarSemSessao(modelos.error);
  }, [modelos.error, voltarSemSessao]);

  const estadoDaLista: EstadoDaLista = naoLista
    ? { tipo: 'nao-lista' }
    : !enderecoListavel
      ? { tipo: 'endereco-invalido' }
      : modelos.isError
        ? {
            tipo: 'erro',
            erro: explicarFalha(modelos.error),
            recarregar: () => {
              void modelos.refetch();
            },
          }
        : modelos.data
          ? modelos.data.listaDisponivel
            ? { tipo: 'pronta', lista: modelos.data }
            : { tipo: 'nao-lista' }
          : { tipo: 'carregando' };

  const avisos = avisosDoRascunho(rascunho, modelos.data ?? null, config.emUso.modeloVetor);
  const ocupado = salvar.isPending || remover.isPending;
  const porQueNao = porQueNaoSalva(rascunho, config);
  const porQueNaoTestar = porQueNaoTesta(rascunho, config.chaveNoAmbiente);
  const sujo = mudou(rascunho, inicial);
  const ajudaDosOpcionais = naoLista ? 'Este protocolo não lê arquivos nem monta vetores neste sistema.' : null;

  return (
    <>
      <IaEmUsoAgora config={config} />

      <HistoriaNaMesa codigo="CMP-PLT-05">
        <Bloco titulo="Motor de IA">
          <div key={versao} className="formulario-grade">
            <Campo id={idDoProtocolo} rotulo="Protocolo" ajuda="É o formato em que o sistema conversa com o provedor.">
              <select
                id={idDoProtocolo}
                value={protocolo}
                className="controle w-full"
                onChange={(evento) => {
                  const escolhido = PROTOCOLOS.find((p) => p === evento.target.value);
                  if (escolhido) mudar({ protocolo: escolhido });
                }}
              >
                {PROTOCOLOS.map((p) => (
                  <option key={p} value={p}>
                    {OPCAO_DO_PROTOCOLO[p]}
                  </option>
                ))}
              </select>
            </Campo>

            <div className="campo-largo">
              <CampoEndereco
                protocolo={protocolo}
                valor={rascunho.endereco}
                aoMudar={(endereco) => {
                  mudar({ endereco });
                }}
                aoSair={(endereco) => {
                  setEnderecoDaLista(normalizarEndereco(endereco));
                }}
              />
            </div>

            <SeletorDeModelo
              rotulo="Modelo para responder"
              tipo="resposta"
              valor={rascunho.modelo}
              lista={estadoDaLista}
              ajuda="Responde às perguntas ao manual, aos resumos, aos planos e aos avisos. Precisa responder em JSON."
              validar={(v) => erroDoModelo(v, false)}
              avisos={avisos.modelo}
              aoMudar={(modelo) => {
                mudar({ modelo });
              }}
            />
            <SeletorDeModelo
              rotulo="Modelo para ler arquivos"
              tipo="resposta"
              opcional
              desligado={naoLista}
              valor={naoLista ? '' : rascunho.modeloArquivo}
              lista={estadoDaLista}
              ajuda={ajudaDosOpcionais ?? 'Lê o PDF do manual escaneado, a foto do cupom e a folha de campo. Vazio usa o modelo para responder.'}
              validar={(v) => erroDoModelo(v, true)}
              avisos={naoLista ? [] : avisos.modeloArquivo}
              aoMudar={(modeloArquivo) => {
                mudar({ modeloArquivo });
              }}
            />
            <SeletorDeModelo
              rotulo="Modelo de vetores"
              tipo="vetor"
              opcional
              desligado={naoLista}
              valor={naoLista ? '' : rascunho.modeloVetor}
              lista={estadoDaLista}
              ajuda={ajudaDosOpcionais ?? 'Monta o índice por sentido dos manuais. Vazio deixa a busca só por palavra.'}
              validar={(v) => erroDoModelo(v, true)}
              avisos={naoLista ? [] : avisos.modeloVetor}
              aoMudar={(modeloVetor) => {
                mudar({ modeloVetor });
              }}
            />
          </div>
        </Bloco>
      </HistoriaNaMesa>

      <Bloco titulo="Teste da configuração">
        <p className="txt-apoio text-pretty text-tinta-secundaria">
          Manda três pedidos curtos ao provedor, com a chave do servidor: uma resposta em JSON, a leitura de um PDF de uma página e um vetor. Custa centavos e fica na trilha da plataforma.
        </p>
        <div className="flex flex-wrap items-center gap-4">
          {/* Consulta à IA: secundário em azul (R02, p.31), e não o laranja,
              que nesta tela é o de salvar. */}
          <Botao
            nivel="consulta"
            disabled={porQueNaoTestar !== null || testar.isPending}
            onClick={() => {
              testar.mutate(paraPedido(rascunho));
            }}
          >
            {testar.isPending ? 'Testando…' : 'Testar configuração'}
          </Botao>
          {porQueNaoTestar ? <span className="txt-apoio text-tinta-secundaria">{porQueNaoTestar}</span> : null}
        </div>
        {teste ? <ResultadoDoTeste teste={teste} /> : null}
      </Bloco>


      {/* Barra de ações fixa (R01): as de voltar à esquerda, a ÚNICA laranja à
          direita. O confirmar da janela de salvar sai secundário, então não há
          segundo laranja nem com ela aberta. */}
      <div className="barra-de-acoes flex-wrap items-center">
        <div className="mr-auto flex flex-wrap items-center gap-2">
          {config.salva ? (
            <AcaoComMotivo
              rotulo="Voltar às variáveis do servidor"
              nivel="destrutivo"
              pergunta="Remover a configuração da IA salva aqui?"
              consequencia="A IA passa a usar as variáveis do servidor em até 30 segundos. Se elas não existirem, a IA fica desligada em todas as empresas."
              rotuloConfirmar="Remover configuração"
              ajudaDoMotivo="Vai para a trilha da plataforma, com a configuração que deixa de valer."
              pendente={ocupado}
              aoConfirmar={(motivo) => {
                salvar.reset();
                remover.mutate(motivo);
              }}
            />
          ) : null}
          {sujo ? (
            <Botao
              nivel="secundario"
              disabled={ocupado}
              onClick={() => {
                recomecar(inicial);
              }}
            >
              Descartar alterações
            </Botao>
          ) : null}
        </div>
        {porQueNao ? <span className="txt-apoio text-tinta-secundaria">{porQueNao}</span> : null}
        <AcaoComMotivo
          rotulo="Salvar configuração"
          nivel="primario"
          pergunta="Salvar a configuração da IA?"
          consequencia={`Vale para todas as empresas em até 30 segundos, sem reiniciar o servidor. A chave continua só no servidor.${
            passouNoTeste(teste, rascunho) ? '' : ' Esta versão ainda não passou no teste.'
          }`}
          rotuloConfirmar="Salvar configuração"
          ajudaDoMotivo="Vai para a trilha da plataforma, com a configuração de antes e a de depois."
          pendente={ocupado || porQueNao !== null}
          aoConfirmar={(motivo) => {
            remover.reset();
            salvar.mutate({ parametros: paraPedido(rascunho), motivo });
          }}
        />
      </div>
    </>
  );
}

/** O endereço: sem exemplo escrito (H12), validado ao sair e pedindo a lista só então. */
function CampoEndereco({
  protocolo,
  valor,
  aoMudar,
  aoSair,
}: {
  readonly protocolo: ProtocoloDaIa;
  readonly valor: string;
  readonly aoMudar: (valor: string) => void;
  readonly aoSair: (valor: string) => void;
}) {
  const id = useId();
  const [erro, setErro] = useState<string | null>(null);
  return (
    <Campo id={id} rotulo="Endereço da API" ajuda={AJUDA_DO_ENDERECO[protocolo]} erro={erro}>
      <input
        id={id}
        type="text"
        inputMode="url"
        autoComplete="off"
        autoCapitalize="off"
        spellCheck={false}
        maxLength={MAIOR_ENDERECO}
        value={valor}
        className="controle w-full"
        onChange={(evento) => {
          setErro(null);
          aoMudar(evento.target.value);
        }}
        onBlur={() => {
          setErro(erroDoEndereco(valor));
          aoSair(valor);
        }}
        {...(erro ? { 'aria-invalid': true, 'aria-describedby': `${id}-erro` } : {})}
      />
    </Campo>
  );
}

const CHAVE_AUSENTE = {
  erro: 'a chave da IA não está no servidor',
  comoResolver:
    'Peça a quem opera o servidor para pôr a chave do provedor em IA_CHAVE, no recurso da API, e reiniciar a API. A configuração pode ser salva antes; a IA liga quando a API voltar com a chave.',
};

function IaEmUsoAgora({ config }: { readonly config: ConfiguracaoDaIaDaPlataforma }) {
  const { emUso: uso, salva } = config;
  return (
    <Bloco titulo="IA em uso agora">
      <dl className="grid grid-cols-[repeat(auto-fill,minmax(var(--campo-min),1fr))] gap-x-6 gap-y-4">
        <Dado rotulo="Origem">{ROTULO_DA_ORIGEM[uso.origem]}</Dado>
        <Dado rotulo="Protocolo">{uso.protocolo ? ROTULO_DO_PROTOCOLO[uso.protocolo] : '—'}</Dado>
        <Dado rotulo="Endereço">{uso.endereco ? <Identificador>{uso.endereco}</Identificador> : '—'}</Dado>
        <Dado rotulo="Responder">{uso.modelo ? <Identificador>{uso.modelo}</Identificador> : '—'}</Dado>
        <Dado rotulo="Ler arquivos">
          <ValorDoUso valor={leituraEmUso(uso)} />
        </Dado>
        <Dado rotulo="Índice por sentido">
          <ValorDoUso valor={indiceEmUso(uso)} />
        </Dado>
        <Dado rotulo="Chave no servidor">{config.chaveNoAmbiente ? 'Presente em IA_CHAVE' : 'Ausente'}</Dado>
        {salva ? (
          <Dado rotulo="Salva em">
            <span className="tabular-nums">{formatarDataHora(new Date(salva.atualizadoEm))}</span>
            {salva.atualizadoPor ? `, por ${salva.atualizadoPor.email}` : ''}
          </Dado>
        ) : null}
      </dl>
      {config.chaveNoAmbiente ? null : <Recado erro={CHAVE_AUSENTE} />}
      {salva ? null : (
        <p className="txt-apoio text-pretty text-tinta-secundaria">
          {uso.origem === 'nenhuma'
            ? 'Nada foi salvo aqui ainda, e as variáveis do servidor não ligam a IA. Ao salvar, esta configuração passa a valer em até 30 segundos e sem reiniciar.'
            : 'Nada foi salvo aqui ainda: a IA usa as variáveis do servidor. Ao salvar, esta configuração passa a valer no lugar delas, em até 30 segundos e sem reiniciar.'}
        </p>
      )}
    </Bloco>
  );
}

function Dado({ rotulo, children }: { readonly rotulo: string; readonly children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <dt className="txt-legenda font-normal text-tinta-secundaria">{rotulo}</dt>
      <dd className="txt-corpo min-w-0 text-tinta">{children}</dd>
    </div>
  );
}

/** Identificador de modelo e endereço em monoespaçada, que quebra em vez de empurrar a página. */
function Identificador({ children }: { readonly children: string }) {
  return <span className="mono txt-corpo break-all">{children}</span>;
}

function ValorDoUso({ valor }: { readonly valor: ValorEmUso }) {
  return 'id' in valor ? <Identificador>{valor.id}</Identificador> : <>{valor.frase}</>;
}

interface LinhaDoTeste {
  readonly etapa: string;
  readonly resultado: EtapaDoTeste | null;
  /** Por que a etapa não se aplica. */
  readonly semEtapa: string;
}

/** O resultado de cada etapa em texto escrito — não é selo de status de equipamento. */
function textoDoResultado(e: EtapaDoTeste | null): string {
  if (!e) return 'Não se aplica';
  return e.ok ? 'Funcionou' : 'Falhou';
}

const COLUNAS_DO_TESTE: readonly Coluna<LinhaDoTeste>[] = [
  { chave: 'etapa', rotulo: 'Etapa', celula: (l) => l.etapa },
  {
    chave: 'resultado',
    rotulo: 'Resultado',
    celula: (l) => {
      const detalhe = l.resultado ? l.resultado.detalhe : l.semEtapa;
      return (
        <span className="flex flex-col">
          <span className="txt-corpo text-tinta">{textoDoResultado(l.resultado)}</span>
          {detalhe ? <span className="txt-apoio text-tinta-secundaria">{detalhe}</span> : null}
        </span>
      );
    },
  },
  {
    chave: 'modelo',
    rotulo: 'Modelo',
    celula: (l) => (l.resultado ? <span className="mono txt-corpo">{l.resultado.modelo}</span> : '—'),
  },
  {
    chave: 'tempo',
    rotulo: 'Tempo',
    alinhamento: 'numero',
    celula: (l) => (l.resultado ? `${formatarInteiro(l.resultado.ms)} ms` : '—'),
  },
  {
    chave: 'fazer',
    rotulo: 'O que fazer',
    historia: 'COL-PLT-06',
    /* A frase inteira quebra dentro da célula: o que fazer cortado não serve. */
    celula: (l) => (
      <span className="txt-apoio block min-w-[var(--campo-min)] whitespace-normal text-tinta-secundaria">
        {l.resultado?.comoResolver ?? '—'}
      </span>
    ),
  },
];

function ResultadoDoTeste({ teste }: { readonly teste: TesteFeito }) {
  const r = teste.resultado;
  const linhas: readonly LinhaDoTeste[] = [
    { etapa: 'Responder', resultado: r.resposta, semEtapa: '' },
    { etapa: 'Ler arquivo', resultado: r.leitura, semEtapa: 'Este protocolo não lê arquivos.' },
    { etapa: 'Vetores', resultado: r.vetores, semEtapa: 'Sem modelo de vetores: a busca fica só por palavra.' },
  ];
  return (
    <div className="flex flex-col gap-2">
      <Tabela
        rotulo="Resultado do teste da IA"
        colunas={COLUNAS_DO_TESTE}
        linhas={linhas}
        /* Três etapas fixas: contagem sem paginação. */
        rodape={<RodapeDeContagem total={linhas.length} />}
        chaveDaLinha={(l) => l.etapa}
        alturaDupla
        vazio={<EstadoVazio titulo="Nenhuma etapa testada" descricao="Teste de novo: o resultado de cada etapa aparece aqui." />}
      />
      <p className="txt-apoio text-tinta-secundaria">
        <span className="tabular-nums">{`Testado em ${formatarDataHora(new Date(r.testadoEm))}.`}</span>
        {' O teste vale para os campos como estavam; se mudou algum, teste de novo.'}
      </p>
    </div>
  );
}
